import { useState } from 'react'
import { toast } from 'sonner'
import { useTranslation } from '@kostbar/i18n'
import { usePairingCode } from '../hooks/use-pairing-code'
import { PairingCodeDisplay } from './pairing-code-display'
import type { PairingCodeResponse } from '@/api/endpoints/edges'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { useRestaurants } from '@/features/products/hooks/use-restaurants'

interface PairingCodeModalProps {
  open: boolean
  onClose: () => void
}

export function PairingCodeModal({ open, onClose }: PairingCodeModalProps) {
  const { t: tEdges } = useTranslation('edges')
  const [restaurantId, setRestaurantId] = useState<string>('')
  const [ttlSeconds, setTtlSeconds] = useState('600')
  const [pairingCode, setPairingCode] = useState<PairingCodeResponse | null>(
    null,
  )

  const { data: restaurantsData } = useRestaurants()
  const mutation = usePairingCode()

  const handleClose = () => {
    setRestaurantId('')
    setTtlSeconds('600')
    setPairingCode(null)
    onClose()
  }

  const handleGenerate = () => {
    if (!restaurantId) {
      toast.error(tEdges('pairingModal.missingRestaurant'))
      return
    }

    const ttl = parseInt(ttlSeconds)
    if (isNaN(ttl) || ttl < 60 || ttl > 3600) {
      toast.error(tEdges('pairingModal.ttlInvalid'))
      return
    }

    mutation.mutate(
      { restaurantId: parseInt(restaurantId), ttlSeconds: ttl },
      {
        onSuccess: (data) => {
          setPairingCode(data)
          toast.success(tEdges('pairingModal.generated'))
        },
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{tEdges('pairingModal.title')}</DialogTitle>
          <DialogDescription>
            {tEdges('pairingModal.description')}
          </DialogDescription>
        </DialogHeader>

        {!pairingCode ? (
          <>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="restaurant">
                  {tEdges('pairingModal.restaurantLabel')}
                </Label>
                <Select value={restaurantId} onValueChange={setRestaurantId}>
                  <SelectTrigger id="restaurant">
                    <SelectValue
                      placeholder={tEdges('pairingModal.restaurantPlaceholder')}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {restaurantsData?.items.map((restaurant) => (
                      <SelectItem
                        key={restaurant.id}
                        value={restaurant.id.toString()}
                      >
                        {restaurant.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ttl">
                  {tEdges('pairingModal.validityLabel')}
                </Label>
                <Input
                  id="ttl"
                  type="number"
                  min="60"
                  max="3600"
                  value={ttlSeconds}
                  onChange={(e) => setTtlSeconds(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  {tEdges('pairingModal.validityHint')}
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={mutation.isPending}
              >
                {tEdges('pairingModal.cancel')}
              </Button>
              <Button onClick={handleGenerate} disabled={mutation.isPending}>
                {mutation.isPending && <Spinner className="w-4 h-4 mr-2" />}
                {tEdges('pairingModal.generate')}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="py-4">
              <PairingCodeDisplay
                code={pairingCode.code}
                expiresAt={pairingCode.expiresAt}
              />
            </div>

            <DialogFooter>
              <Button onClick={handleClose}>
                {tEdges('pairingModal.close')}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
